import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useDash } from './DashProvider'
import './dash.css'

/**
 * Der Auslöser: sieben Klicks auf die Wortmarke.
 *
 * WARUM SIEBEN UND WARUM MIT ZÄHLER. Ein einzelner Klick wäre kein Geheimnis,
 * sondern ein Knopf, den man versehentlich trifft. Sieben trifft niemand
 * versehentlich. Aber sieben Klicks ohne jede Rückmeldung findet auch niemand
 * absichtlich — deshalb meldet sich ab dem dritten ein kleiner Zähler. Bis
 * dahin ist nichts zu sehen; wer nur die Kopfleiste anfasst, merkt nie, dass es
 * etwas zu finden gibt.
 *
 * DIE WORTMARKE BLEIBT, WAS SIE IST. Kein Zeiger, kein Hover, keine ARIA-Rolle,
 * kein Fokusring: sie ist eine Überschrift und kein Bedienelement, und ein
 * Easter Egg, das sich ankündigt, ist keines. Wer mit der Tastatur navigiert
 * oder einen Screenreader benutzt, stolpert deshalb auch nicht darüber.
 *
 * WER `prefers-reduced-motion` GESETZT HAT, KOMMT NICHT HEREIN. Ein Endlosläufer
 * ist Bewegung von der ersten bis zur letzten Sekunde; ihn zu entschärfen hiesse,
 * ihn abzuschaffen. Die ehrlichere Antwort ist, ihn gar nicht erst anzubieten.
 */

export function DashKnock({
  className,
  children,
}: {
  /*
    Der Auslöser IST die Markenfläche und sitzt nicht darin. Als eigener
    Container mit `display: contents` hatte er keine eigene Box — ein Klick in
    den Abstand zwischen Bildmarke und Schriftzug traf die Elternfläche, lief an
    ihm vorbei und wurde nicht gezählt. Wer die Wortmarke anklickt, trifft
    manchmal genau diesen Abstand.
  */
  className?: string
  children: ReactNode
}) {
  const { knock, running } = useDash()
  const [left, setLeft] = useState<number | null>(null)
  const timer = useRef<number | null>(null)

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    },
    [],
  )

  const onClick = useCallback(() => {
    if (running) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    /* Ob und ab wann sich der Zähler meldet, entscheidet der Kern — diese Datei
       zeigt nur an, was er sagt. */
    const { remaining, hint } = knock()
    if (!hint) {
      setLeft(null)
      return
    }
    setLeft(remaining)
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setLeft(null), 1500)
  }, [knock, running])

  return (
    <div className={['dash-knock', className].filter(Boolean).join(' ')} onClick={onClick}>
      {children}
      {left !== null && (
        <span className="dash-knock__toast" aria-hidden="true">
          noch {left}
        </span>
      )}
    </div>
  )
}
